import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Book, Trophy, Target, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { bookInfo, chapters, testimonials } from '../data/mockData';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden brick-texture">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(10, 10, 10, 0.85), rgba(10, 10, 10, 0.85)), url('https://customer-assets.emergentagent.com/job_028c76be-fda9-4451-9c6f-c3c6a93b4016/artifacts/soih5fjl_IMG_0014.avif')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        />
        
        {/* Content */}
        <div className="container mx-auto px-4 relative z-10 pt-20">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            {/* Logo/Monogram */}
            <div className="inline-block mb-6">
              <div className="text-6xl md:text-8xl font-bold text-gold-gradient">
                CE
              </div>
            </div>

            {/* Title */}
            <h1 className="text-5xl md:text-7xl font-bold leading-tight">
              <span className="text-muro-gold tracking-wider">EL MURO</span>
              <br />
              <span className="text-muro-text tracking-widest">ENDLER</span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-muro-text-muted max-w-3xl mx-auto font-light">
              {bookInfo.subtitle}
            </p>

            {/* Tagline */}
            <div className="py-8">
              <div className="inline-block px-8 py-4 glass-morphism rounded-lg">
                <p className="text-lg md:text-xl text-muro-gold font-medium italic">
                  "{bookInfo.tagline}"
                </p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Link to="/comprar">
                <Button className="btn-gold px-8 py-6 text-lg rounded-md flex items-center space-x-2">
                  <span>Comprar Ahora</span>
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/libro">
                <Button className="btn-gold-outline px-8 py-6 text-lg rounded-md flex items-center space-x-2">
                  <Book className="w-5 h-5" />
                  <span>Explorar Capítulos</span>
                </Button>
              </Link>
            </div>

            {/* Release Info */}
            <div className="pt-8 text-muro-text-muted text-sm">
              <p>Preventa disponible • Lanzamiento: Junio 2026</p>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-muro-gold rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-muro-gold rounded-full"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-muro-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Image */}
              <div className="relative">
                <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-2xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_028c76be-fda9-4451-9c6f-c3c6a93b4016/artifacts/t3bx875x_IMG_0010.jpeg"
                    alt="Christiane Endler"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-muro-gold px-8 py-4 rounded-lg shadow-gold-lg">
                  <p className="text-muro-black font-bold text-xl">Campeona de Europa</p>
                  <p className="text-muro-black text-sm">Lyon 2022</p>
                </div>
              </div>

              {/* Content */}
              <div className="space-y-6">
                <div>
                  <h2 className="text-4xl md:text-5xl font-bold text-muro-text mb-4 gold-accent-line">
                    No es solo una biografía
                  </h2>
                  <p className="text-xl text-muro-gold font-medium">
                    Es un manual de vida
                  </p>
                </div>
                
                <p className="text-muro-text-muted text-lg leading-relaxed">
                  {bookInfo.description}
                </p>

                <div className="space-y-4 pt-4">
                  <div className="flex items-start space-x-4">
                    <div className="bg-muro-gold/10 p-3 rounded-lg">
                      <Shield className="w-6 h-6 text-muro-gold" />
                    </div>
                    <div>
                      <h3 className="text-muro-text font-semibold mb-1">
                        Para toda persona valiente
                      </h3>
                      <p className="text-muro-text-muted text-sm">
                        No solo para futbolistas. Para cualquiera que construye su defensa contra las adversidades.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-muro-gold/10 p-3 rounded-lg">
                      <Target className="w-6 h-6 text-muro-gold" />
                    </div>
                    <div>
                      <h3 className="text-muro-text font-semibold mb-1">
                        10 ladrillos para tu muro
                      </h3>
                      <p className="text-muro-text-muted text-sm">
                        Principios concretos que Tiane aplicó para convertirse en la mejor del mundo.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-muro-gold/10 p-3 rounded-lg">
                      <Trophy className="w-6 h-6 text-muro-gold" />
                    </div>
                    <div>
                      <h3 className="text-muro-text font-semibold mb-1">
                        De Las Condes a Lyon
                      </h3>
                      <p className="text-muro-text-muted text-sm">
                        La historia real de cómo una niña tímida se convirtió en leyenda.
                      </p>
                    </div>
                  </div>
                </div>

                <Link to="/libro">
                  <Button className="btn-gold-outline px-6 py-3 rounded-md mt-4">
                    Descubre más sobre el libro
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chapters Preview */}
      <section className="py-20 brick-texture">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-muro-text mb-4">
              Los <span className="text-gold-gradient">8 Capítulos</span> del Muro
            </h2>
            <p className="text-muro-text-muted text-lg max-w-2xl mx-auto">
              Cada capítulo es un ladrillo. Cada lección, cemento. Juntos, construyen tu defensa inquebrantable.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {chapters.slice(0, 4).map((chapter) => (
              <div
                key={chapter.id}
                className="card-hover bg-muro-gray-900 p-6 rounded-lg"
              >
                <div className="text-muro-gold font-bold text-sm mb-2">
                  {chapter.number}
                </div>
                <h3 className="text-xl font-bold text-muro-text mb-2">
                  {chapter.title}
                </h3>
                <p className="text-muro-text-muted text-sm mb-4">
                  {chapter.subtitle}
                </p>
                <div className="h-1 bg-muro-gold/20 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-muro-gold rounded-full"
                    style={{ width: `${(chapter.id / 8) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/libro">
              <Button className="btn-gold px-8 py-4 text-lg rounded-md flex items-center space-x-2 mx-auto">
                <span>Ver todos los capítulos</span>
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-muro-dark">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-muro-text mb-4">
              Lo que dicen de <span className="text-gold-gradient">Tiane</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonials.slice(0, 4).map((testimonial) => (
              <div
                key={testimonial.id}
                className="glass-morphism p-8 rounded-lg"
              >
                <p className="text-muro-text text-lg italic mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-muro-gold/20 rounded-full flex items-center justify-center">
                    <span className="text-muro-gold font-bold text-xl">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-muro-text font-semibold">
                      {testimonial.name}
                    </p>
                    <p className="text-muro-text-muted text-sm">
                      {testimonial.role}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 brick-texture">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-6xl font-bold text-muro-text">
              Construye tu <span className="text-gold-gradient">propio muro</span>
            </h2>
            <p className="text-xl text-muro-text-muted max-w-2xl mx-auto">
              Este no es solo el muro de Tiane. Es una invitación a construir el tuyo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Link to="/comprar">
                <Button className="btn-gold px-10 py-6 text-xl rounded-md flex items-center space-x-2">
                  <span>Comprar El Muro Endler</span>
                  <ArrowRight className="w-6 h-6" />
                </Button>
              </Link>
            </div>
            <p className="text-muro-text-muted text-sm pt-4">
              Preventa especial • ${bookInfo.price.toLocaleString('es-CL')} CLP
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
